import { useEffect, useState } from "react";
import { X, Wallet, Loader2, Copy, CheckCircle2, Zap, Info } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const AMOUNTS = [20, 50, 100, 250, 500];
const NETWORKS = [
  { id: "TRC20",   label: "TRON (TRC20)",     desc: "Taxa ~$1 · Recomendado" },
  { id: "BEP20",   label: "BSC (BEP20)",      desc: "Taxa ~$0.30" },
  { id: "POLYGON", label: "Polygon",          desc: "Taxa ~$0.01" },
  { id: "ERC20",   label: "Ethereum (ERC20)", desc: "Taxa $5-30" },
];
const MIN_NOWP_USD = 20;
const MAX_USD = 2000;

export default function DepositModal({ open, onClose, onCredited }) {
  const [mode, setMode] = useState("nowpayments");   // "nowpayments" | "direct"
  const [amount, setAmount] = useState(20);
  const [custom, setCustom] = useState("");
  const [network, setNetwork] = useState("TRC20");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // Direct deposit state
  const [platformWallet, setPlatformWallet] = useState(null);
  const [txHash, setTxHash] = useState("");
  const [fromAddress, setFromAddress] = useState("");
  const [copied, setCopied] = useState(false);
  const [directDone, setDirectDone] = useState(null);

  useEffect(() => {
    if (!open || mode !== "direct" || platformWallet) return;
    fetch(`${API}/api/deposits/platform-wallet`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => { if (d?.address) setPlatformWallet(d); })
      .catch(() => {});
  }, [open, mode, platformWallet]);

  if (!open) return null;

  const finalAmount = Number(custom) > 0 ? Number(custom) : amount;

  const submitNowp = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${API}/api/deposits/create`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount_usd: finalAmount, network }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Falha ao criar depósito");
      window.location.href = d.invoice_url;
    } catch (e) {
      setErr(e.message);
      setBusy(false);
    }
  };

  const submitDirect = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${API}/api/deposits/direct`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tx_hash: txHash.trim(), from_address: fromAddress.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Falha ao verificar depósito");
      setDirectDone(d);
      onCredited?.(d);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const copyAddr = async () => {
    if (!platformWallet?.address) return;
    try { await navigator.clipboard.writeText(platformWallet.address); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch (err) { console.warn(err); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4" data-testid="deposit-modal">
      <div className="w-full max-w-md rounded-sm border border-primary/40 bg-gradient-to-b from-[#050a18] to-[#020408] p-6 space-y-4 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Wallet className="h-5 w-5 text-primary" />
            <div>
              <p className="font-mono text-[10px] tracking-[0.3em] text-secondary">// COMBUSTÍVEL CÓSMICO</p>
              <h2 className="font-display font-black text-xl tracking-tighter text-white">DEPOSITAR USDT</h2>
            </div>
          </div>
          <button onClick={onClose} data-testid="deposit-close" className="p-2 border border-primary/30 rounded-sm hover:bg-primary/10 text-primary">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Mode tabs */}
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => { setMode("nowpayments"); setErr(null); }}
            data-testid="deposit-mode-nowpayments"
            className={`rounded-sm border px-3 py-3 text-left ${mode === "nowpayments" ? "border-primary bg-primary/10" : "border-primary/30 hover:border-primary/60"}`}>
            <div className="font-mono text-[10px] tracking-widest text-white">💳 RÁPIDO (QR)</div>
            <div className="font-mono text-[9px] text-muted-foreground mt-0.5">$20-$2000 · via NOWPayments</div>
          </button>
          <button onClick={() => { setMode("direct"); setErr(null); }}
            data-testid="deposit-mode-direct"
            className={`rounded-sm border px-3 py-3 text-left ${mode === "direct" ? "border-emerald-400 bg-emerald-500/10" : "border-primary/30 hover:border-primary/60"}`}>
            <div className="font-mono text-[10px] tracking-widest text-emerald-300">🔗 DIRETO (avançado)</div>
            <div className="font-mono text-[9px] text-muted-foreground mt-0.5">A partir de $1 · TRC20</div>
          </button>
        </div>

        {mode === "nowpayments" && (
          <>
            <div>
              <p className="font-mono text-[10px] tracking-widest text-muted-foreground mb-2">VALOR (USD)</p>
              <div className="grid grid-cols-5 gap-2 mb-2">
                {AMOUNTS.map((a) => (
                  <button key={a} onClick={() => { setAmount(a); setCustom(""); }}
                    data-testid={`deposit-amount-${a}`}
                    className={`rounded-sm border px-3 py-2 font-mono text-xs tracking-widest ${amount === a && !custom ? "border-primary bg-primary/20 text-primary" : "border-primary/30 text-muted-foreground hover:border-primary/60"}`}>
                    ${a}
                  </button>
                ))}
              </div>
              <input type="number" min={MIN_NOWP_USD} max={MAX_USD} step="1" placeholder={`Ou digite outro valor (min $${MIN_NOWP_USD}, max $${MAX_USD})`}
                value={custom} onChange={(e) => setCustom(e.target.value)}
                data-testid="deposit-custom-amount"
                className="w-full bg-black/60 border border-primary/30 rounded-sm px-3 py-2 font-mono text-sm text-primary placeholder-muted-foreground/60" />
              <p className="mt-1 font-mono text-[9px] text-amber-300/70 tracking-widest">
                Mínimo ${MIN_NOWP_USD} nesta modalidade. Pra valores menores use DEPÓSITO DIRETO.
              </p>
            </div>

            <div>
              <p className="font-mono text-[10px] tracking-widest text-muted-foreground mb-2">REDE USDT</p>
              <div className="space-y-2">
                {NETWORKS.map((n) => (
                  <button key={n.id} onClick={() => setNetwork(n.id)}
                    data-testid={`deposit-network-${n.id}`}
                    className={`w-full text-left rounded-sm border px-3 py-2 ${network === n.id ? "border-primary bg-primary/10" : "border-primary/30 hover:border-primary/60"}`}>
                    <div className="font-mono text-xs tracking-widest text-white">{n.label}</div>
                    <div className="font-mono text-[10px] text-muted-foreground">{n.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {err && <div className="rounded-sm border border-red-500/50 bg-red-500/10 p-3 text-sm text-red-300 font-mono">{err}</div>}

            <button onClick={submitNowp} disabled={busy || finalAmount < MIN_NOWP_USD || finalAmount > MAX_USD}
              data-testid="deposit-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-mono tracking-widest text-xs px-5 py-3 disabled:opacity-40">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : `DEPOSITAR $${finalAmount || 0} VIA ${network} ▶`}
            </button>
            <p className="text-center font-mono text-[10px] text-muted-foreground">
              Você será redirecionado ao checkout seguro do NOWPayments
            </p>
          </>
        )}

        {mode === "direct" && (
          <>
            {directDone ? (
              <div className="space-y-3">
                <div className="rounded-sm border border-emerald-400/50 bg-emerald-500/10 p-4 flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-emerald-300 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-mono text-[10px] tracking-widest text-emerald-300">DEPÓSITO CREDITADO</p>
                    <p className="font-display font-black text-3xl text-emerald-300 tracking-tighter">+${directDone.amount_usd.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground font-mono mt-1">Já está disponível na sua carteira interna.</p>
                  </div>
                </div>
                <button onClick={onClose} data-testid="deposit-direct-done"
                  className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-mono tracking-widest text-xs px-5 py-3">
                  FECHAR
                </button>
              </div>
            ) : (
              <>
                <div className="rounded-sm border border-emerald-400/40 bg-emerald-500/5 p-3 text-xs font-mono text-emerald-200 flex items-start gap-2">
                  <Zap className="h-4 w-4 mt-0.5 shrink-0 text-emerald-300" />
                  <div className="space-y-1">
                    <p className="text-emerald-300 font-bold tracking-widest">SEM MÍNIMO ALTO · SEM INTERMEDIÁRIO</p>
                    <p>Manda USDT-TRC20 pra nossa carteira e cola o hash da transação. A blockchain confirma → seu saldo sobe automático.</p>
                  </div>
                </div>

                <div>
                  <p className="font-mono text-[10px] tracking-widest text-muted-foreground mb-1">1. NOSSA CARTEIRA USDT-TRC20</p>
                  {platformWallet ? (
                    <div className="flex items-center gap-2 rounded-sm border border-emerald-400/40 bg-black/50 px-3 py-2">
                      <span className="font-mono text-[11px] text-emerald-300 break-all flex-1" data-testid="deposit-direct-address">{platformWallet.address}</span>
                      <button onClick={copyAddr} data-testid="deposit-direct-copy"
                        className="inline-flex items-center gap-1 rounded-sm border border-emerald-400/40 hover:bg-emerald-500/10 text-emerald-300 font-mono text-[9px] tracking-widest px-2 py-1">
                        {copied ? <><CheckCircle2 className="h-3 w-3" /> OK</> : <><Copy className="h-3 w-3" /> COPIAR</>}
                      </button>
                    </div>
                  ) : (
                    <div className="rounded-sm border border-primary/30 bg-black/50 px-3 py-2 font-mono text-[10px] text-muted-foreground">Carregando endereço...</div>
                  )}
                  <p className="mt-1 font-mono text-[9px] text-amber-300/80 tracking-widest">
                    ⚠️ SÓ ENVIE USDT NA REDE TRC20 (TRON). Qualquer outra rede: dinheiro perdido.
                  </p>
                </div>

                <div>
                  <p className="font-mono text-[10px] tracking-widest text-muted-foreground mb-1">2. COLE O TX HASH</p>
                  <input value={txHash} onChange={(e) => setTxHash(e.target.value)}
                    data-testid="deposit-direct-txhash"
                    placeholder="Ex: 3a5f2e1b... (64 chars, veja no Tronscan)"
                    className="w-full bg-black/60 border border-emerald-400/30 rounded-sm px-3 py-2 font-mono text-[11px] text-emerald-300 placeholder-muted-foreground/60" />
                  <p className="mt-1 font-mono text-[9px] text-muted-foreground flex items-start gap-1">
                    <Info className="h-3 w-3 mt-0.5 shrink-0" />
                    Na sua wallet (Trust/Binance/etc), depois de enviar, abre os detalhes da tx e copia o "Transaction ID" ou "Hash".
                  </p>
                </div>

                <div>
                  <p className="font-mono text-[10px] tracking-widest text-muted-foreground mb-1">3. SEU ENDEREÇO DE ORIGEM (TRC20)</p>
                  <input value={fromAddress} onChange={(e) => setFromAddress(e.target.value)}
                    data-testid="deposit-direct-from-address"
                    placeholder="Ex: TXYZ... (endereço DE ONDE você enviou o USDT)"
                    className="w-full bg-black/60 border border-emerald-400/30 rounded-sm px-3 py-2 font-mono text-[11px] text-emerald-300 placeholder-muted-foreground/60" />
                  <p className="mt-1 font-mono text-[9px] text-amber-200/80 flex items-start gap-1">
                    <Info className="h-3 w-3 mt-0.5 shrink-0" />
                    Este é o endereço da SUA carteira (não o nosso). Necessário para provar que o TX é seu.
                  </p>
                </div>

                {err && <div className="rounded-sm border border-red-500/50 bg-red-500/10 p-3 text-sm text-red-300 font-mono">{err}</div>}

                <button onClick={submitDirect} disabled={busy || txHash.trim().length < 40 || !fromAddress.trim().startsWith("T")}
                  data-testid="deposit-direct-submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-sm bg-emerald-500 text-black hover:bg-emerald-400 disabled:opacity-40 font-mono tracking-widest text-xs px-5 py-3">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "VERIFICAR E CREDITAR ▶"}
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
